import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import StoryShare from "../../components/StoryShare";
import { getJob } from "../../lib/api";
import { getCompany, getEmployer } from "../../data/companies";
import { T } from "../../theme";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/* ══════════ LAYOUT ══════════ */
const Page = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: ${T.bg};
`;

const Wrap = styled.main`
  flex: 1;
  max-width: 940px;
  margin: 0 auto;
  padding: clamp(28px, 5vw, 52px) clamp(16px, 4vw, 32px) 96px;
  width: 100%;
  animation: ${fadeUp} 0.4s ease both;
`;

const Back = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  color: ${T.muted};
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 32px;
  padding: 6px 0;
  transition: color .12s;
  &:hover { color: ${T.text}; }
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: 1fr 288px;
  gap: 24px;
  align-items: start;

  @media (max-width: 740px) {
    grid-template-columns: 1fr;
  }
`;

/* ══════════ COMPANY BANNER ══════════ */
const CompanyHero = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 18px 20px;
  border-radius: 16px;
  margin-bottom: 26px;
  background: linear-gradient(120deg, ${(p) => p.$c}2e, ${(p) => p.$c}0a 70%);
  border: 1px solid ${(p) => p.$c}3a;
`;

const CompanyHeroLogo = styled.img`
  width: 54px;
  height: 54px;
  border-radius: 14px;
  flex-shrink: 0;
  object-fit: cover;
  background: #fff;
  box-shadow: 0 4px 16px ${(p) => p.$c}55;
`;

const CompanyHeroFallback = styled.div`
  width: 54px;
  height: 54px;
  border-radius: 14px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  font-weight: 800;
  color: #fff;
  background: ${(p) => p.$c};
  box-shadow: 0 4px 16px ${(p) => p.$c}55;
`;

const CompanyHeroName = styled.p`
  font-size: 1.02rem;
  font-weight: 800;
  color: ${T.text};
  letter-spacing: -0.01em;
`;

const CompanyHeroSeg = styled.p`
  font-size: 0.76rem;
  font-weight: 600;
  color: ${T.muted2};
  margin-top: 2px;
`;

const CompanyHeroTag = styled.span`
  margin-left: auto;
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${(p) => p.$c};
  background: ${(p) => p.$c}1f;
  border: 1px solid ${(p) => p.$c}40;
  padding: 4px 10px;
  border-radius: 999px;
  white-space: nowrap;

  @media (max-width: 520px) { display: none; }
`;

/* ══════════ JOB HEADER ══════════ */
const JobHeaderWrap = styled.div`
  padding: 0 0 28px;
  border-bottom: 1px solid ${T.border};
  margin-bottom: 32px;
`;

const Eyebrow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 0.71rem;
  font-weight: 700;
  padding: 3px 11px;
  border-radius: 999px;
  background: rgba(155,81,255,.12);
  color: var(--purple);
  border: 1px solid rgba(155,81,255,.25);
  margin-bottom: 14px;

  &::before {
    content: "";
    width: 5px; height: 5px;
    border-radius: 50%;
    background: currentColor;
    flex-shrink: 0;
  }
`;

const Title = styled.h1`
  font-size: clamp(1.7rem, 4vw, 2.5rem);
  font-weight: 900;
  letter-spacing: -0.032em;
  color: ${T.text};
  line-height: 1.12;
  margin-bottom: 18px;
`;

const MetaRow = styled.div`
  display: flex;
  gap: 7px;
  flex-wrap: wrap;
`;

const MetaBadge = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: 0.76rem;
  font-weight: 600;
  padding: 4px 11px;
  border-radius: 7px;
  white-space: nowrap;
  background: ${T.card2};
  border: 1px solid ${T.border};
  color: ${T.muted};

  &.salary {
    background: rgba(74,222,128,.1);
    border-color: rgba(74,222,128,.22);
    color: #4ade80;
    font-weight: 700;
  }
`;

/* ══════════ CONTENT ══════════ */
const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${T.border};
  margin: 28px 0;
`;

const Section = styled.section`
  margin-bottom: 32px;
`;

const STitle = styled.h2`
  font-size: 0.8rem;
  font-weight: 800;
  color: ${T.text};
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;

  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background: ${T.border};
  }
`;

const Prose = styled.p`
  font-size: 0.92rem;
  color: ${T.muted};
  line-height: 1.8;
  white-space: pre-wrap;
`;

const BulletList = styled.ul`
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const BulletItem = styled.li`
  display: flex;
  gap: 10px;
  font-size: 0.9rem;
  color: ${T.muted};
  line-height: 1.6;

  &::before {
    content: "→";
    color: var(--purple);
    flex-shrink: 0;
    font-size: 0.78rem;
    margin-top: 3px;
  }
`;

const ProcessList = styled.ol`
  list-style: none;
  counter-reset: step;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ProcessItem = styled.li`
  counter-increment: step;
  display: flex;
  gap: 14px;
  align-items: flex-start;
  font-size: 0.9rem;
  color: ${T.muted};
  line-height: 1.55;

  &::before {
    content: counter(step);
    flex-shrink: 0;
    width: 26px; height: 26px;
    border-radius: 50%;
    background: var(--purple-soft);
    border: 1px solid color-mix(in srgb, var(--purple) 30%, transparent);
    color: var(--purple);
    font-size: 0.72rem;
    font-weight: 800;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 0;
  }
`;

/* ══════════ SIDEBAR ══════════ */
const Sidebar = styled.aside`
  position: sticky;
  top: 82px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  @media (max-width: 740px) {
    position: static;
    order: -1;
  }
`;

const SideCard = styled.div`
  background: ${T.card};
  border: 1px solid ${T.border};
  border-radius: 16px;
  padding: 20px;
`;

const SideTitle = styled.p`
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.13em;
  text-transform: uppercase;
  color: ${T.muted2};
  margin-bottom: 14px;
`;

const ApplyBtn = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: var(--purple);
  color: #fff;
  font-weight: 800;
  font-size: 0.97rem;
  padding: 15px 20px;
  border-radius: 12px;
  width: 100%;
  text-align: center;
  box-shadow: 0 4px 18px rgba(155,81,255,.32);
  transition: filter .14s, transform .12s, box-shadow .14s;

  &:hover {
    filter: brightness(1.1);
    transform: translateY(-2px);
    box-shadow: 0 8px 28px rgba(155,81,255,.42);
  }
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 9px 0;
  border-bottom: 1px solid ${T.border};

  &:last-child { border-bottom: none; padding-bottom: 0; }
  &:first-child { padding-top: 0; }
`;

const InfoLabel = styled.span`
  font-size: 0.77rem;
  color: ${T.muted2};
`;

const InfoValue = styled.span`
  font-size: 0.77rem;
  font-weight: 600;
  color: ${T.text};
  text-align: right;
  max-width: 56%;
`;

const SalaryValue = styled.span`
  font-size: 0.82rem;
  font-weight: 700;
  color: #4ade80;
  text-align: right;
`;

const ShareBtn = styled.button`
  all: unset;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  width: 100%;
  box-sizing: border-box;
  padding: 11px;
  border: 1px solid ${T.border};
  border-radius: 10px;
  font-size: 0.83rem;
  font-weight: 600;
  color: ${T.muted};
  cursor: pointer;
  transition: border-color .12s, color .12s, background .12s;
  &:hover { border-color: var(--purple); color: var(--purple); background: var(--purple-soft); }
`;

/* ── skeleton ── */
const SkBlock = styled.div`
  border-radius: 10px;
  background: ${T.card};
  border: 1px solid ${T.border};
  animation: pulse 1.6s ease-in-out infinite;
  @keyframes pulse { 0%,100%{opacity:.5} 50%{opacity:.22} }
`;

/* ── helpers ── */
const safeText = (v) => (typeof v === "string" ? v : (v?.title || v?.obs || ""));
const normalize = (val) => {
  if (!val) return [];
  if (Array.isArray(val)) return val.map(safeText).filter(Boolean);
  if (typeof val === "string") return val.split("\n").filter(Boolean);
  return [];
};

/* ── SEO: structured data JobPosting (Google Empregos) ── */
const escHtml = (s = "") =>
  String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

/** Mapeia o regime da vaga para o employmentType do schema.org. */
function employmentType(regime = "") {
  const r = regime.toLowerCase();
  if (/est[áa]g/.test(r)) return "INTERN";
  if (/pj|pessoa jur|freela|comiss|aut[ôo]n|prestad/.test(r)) return "CONTRACTOR";
  if (/meio per[íi]odo|part/.test(r)) return "PART_TIME";
  if (/tempor/.test(r)) return "TEMPORARY";
  return "FULL_TIME";
}

/** Descrição HTML completa (Google exige description rica). */
function buildDescriptionHtml(job) {
  const parts = [];
  if (job.resumo) parts.push(`<p>${escHtml(job.resumo)}</p>`);
  if (job.descricao) parts.push(`<p>${escHtml(job.descricao)}</p>`);
  const sect = (title, val) => {
    const arr = normalize(val);
    if (arr.length) parts.push(`<h3>${title}</h3><ul>${arr.map((x) => `<li>${escHtml(x)}</li>`).join("")}</ul>`);
  };
  sect("Responsabilidades", job.responsabilidades);
  sect("Requisitos", job.requisitos);
  sect("Benefícios", job.beneficios);
  return parts.join("") || `<p>${escHtml(job.title)}</p>`;
}

/** Monta o objeto JobPosting (schema.org) da vaga. */
function buildJobPostingLd(job, co, employer) {
  const remote = !job.local || /remoto|remote|home\s*office|híbrido|hibrido/i.test(job.local);
  const ld = {
    "@context": "https://schema.org/",
    "@type": "JobPosting",
    title: job.title,
    description: buildDescriptionHtml(job),
    identifier: { "@type": "PropertyValue", name: "Guebly", value: job.slug },
    datePosted: (job.published_at || job.created_at || "").slice(0, 10),
    employmentType: employmentType(job.regime),
    hiringOrganization: {
      "@type": "Organization",
      name: employer?.razao || co?.name || "Guebly",
      sameAs: "https://www.guebly.com.br",
      logo: "https://www.guebly.com.br/guebly.png",
    },
    directApply: true,
  };
  if (job.expires_at) ld.validThrough = job.expires_at;
  if (remote) {
    ld.jobLocationType = "TELECOMMUTE";
    ld.applicantLocationRequirements = { "@type": "Country", name: "Brasil" };
  } else {
    ld.jobLocation = {
      "@type": "Place",
      address: { "@type": "PostalAddress", addressLocality: job.local, addressCountry: "BR" },
    };
  }
  return ld;
}

/* ══════════════════════════════════
   COMPONENT
══════════════════════════════════ */
export default function Vaga() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [job, setJob]         = useState(null);
  const [loading, setLoading] = useState(true);
  const [shared, setShared]   = useState(false);

  useEffect(() => {
    getJob(slug)
      .then((data) => { if (!data) navigate("/"); else setJob(data); })
      .catch(() => navigate("/"))
      .finally(() => setLoading(false));
  }, [slug, navigate]);

  useEffect(() => {
    if (job) document.title = `${job.title} · Guebly Carreiras`;
    return () => { document.title = "Guebly Carreiras — Vagas abertas"; };
  }, [job]);

  // SEO: JobPosting (Google Empregos) + meta description + canonical
  useEffect(() => {
    if (!job) return;
    const co = getCompany(job.empresa);
    const employer = getEmployer(job.empresa);

    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = "jobposting-ld";
    ld.textContent = JSON.stringify(buildJobPostingLd(job, co, employer));
    document.head.appendChild(ld);

    const url = `https://vagas.guebly.com.br/vagas/${job.slug}`;
    const desc = (job.resumo || job.descricao || job.title || "").slice(0, 300);
    const title = `${job.title} · Guebly Carreiras`;
    const img = "https://vagas.guebly.com.br/banner.jpg";

    const upsertMeta = (key, value, isProp) => {
      const sel = isProp ? `meta[property="${key}"]` : `meta[name="${key}"]`;
      let el = document.head.querySelector(sel);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(isProp ? "property" : "name", key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", value);
    };
    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", url);

    upsertMeta("description", desc);
    upsertMeta("og:title", title, true);
    upsertMeta("og:description", desc, true);
    upsertMeta("og:type", "website", true);
    upsertMeta("og:url", url, true);
    upsertMeta("og:image", img, true);
    upsertMeta("twitter:card", "summary_large_image");
    upsertMeta("twitter:title", title);
    upsertMeta("twitter:description", desc);
    upsertMeta("twitter:image", img);

    return () => { ld.remove(); };
  }, [job]);

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({ title: job?.title, url: window.location.href }).catch(() => {});
    } else {
      navigator.clipboard?.writeText(window.location.href).then(() => {
        setShared(true);
        setTimeout(() => setShared(false), 2000);
      });
    }
  };

  if (loading) {
    return (
      <Page>
        <Header />
        <Wrap>
          <SkBlock style={{ width: 80, height: 16, marginBottom: 32 }} />
          <Layout>
            <div>
              <SkBlock style={{ width: "45%", height: 16, marginBottom: 12 }} />
              <SkBlock style={{ width: "78%", height: 52, marginBottom: 20 }} />
              <SkBlock style={{ width: "55%", height: 18, marginBottom: 36 }} />
              <SkBlock style={{ height: 130, marginBottom: 16 }} />
              <SkBlock style={{ height: 90 }} />
            </div>
            <div>
              <SkBlock style={{ height: 220, marginBottom: 12 }} />
              <SkBlock style={{ height: 160 }} />
            </div>
          </Layout>
        </Wrap>
        <Footer />
      </Page>
    );
  }

  if (!job) return null;

  const co                = getCompany(job.empresa);
  const employer          = getEmployer(job.empresa);
  const responsabilidades = normalize(job.responsabilidades);
  const requisitos        = normalize(job.requisitos);
  const diferenciais      = normalize(job.diferenciais);
  const beneficios        = normalize(job.beneficios);
  const processo          = normalize(job.processo_seletivo);

  return (
    <Page>
      <Header />
      <Wrap id="main-content">
        <Back onClick={() => navigate("/")} aria-label="Voltar para a lista de vagas">
          <span aria-hidden="true">←</span> Voltar às vagas
        </Back>

        <Layout>
          {/* ── CONTEÚDO PRINCIPAL ── */}
          <div>
            <CompanyHero $c={co.color}>
              {co.logo ? (
                <CompanyHeroLogo $c={co.color} src={co.logo} alt={`Logo ${co.name}`} />
              ) : (
                <CompanyHeroFallback $c={co.color} aria-hidden="true">{co.initials}</CompanyHeroFallback>
              )}
              <div>
                <CompanyHeroName>{co.name}</CompanyHeroName>
                <CompanyHeroSeg>{co.segment}</CompanyHeroSeg>
              </div>
              <CompanyHeroTag $c={co.color}>Vaga nesta empresa</CompanyHeroTag>
            </CompanyHero>

            <JobHeaderWrap>
              {job.area && <Eyebrow>{job.area}</Eyebrow>}
              <Title>{job.title}</Title>
              <MetaRow>
                {job.regime  && <MetaBadge>{job.regime}</MetaBadge>}
                {job.nivel   && <MetaBadge>{job.nivel}</MetaBadge>}
                <MetaBadge>{job.local || "Remoto"}</MetaBadge>
                {job.faixa   && <MetaBadge className="salary"><span aria-hidden="true">💰</span> {job.faixa}</MetaBadge>}
              </MetaRow>
            </JobHeaderWrap>

            {(job.resumo || job.descricao) && (
              <Section>
                <STitle>Sobre a vaga</STitle>
                <Prose>{job.resumo || job.descricao}</Prose>
              </Section>
            )}

            {job.resumo && job.descricao && (
              <Section>
                <STitle>Descrição completa</STitle>
                <Prose>{job.descricao}</Prose>
              </Section>
            )}

            {responsabilidades.length > 0 && (
              <>
                <Divider />
                <Section>
                  <STitle>Responsabilidades</STitle>
                  <BulletList>
                    {responsabilidades.map((r, i) => <BulletItem key={i}>{r}</BulletItem>)}
                  </BulletList>
                </Section>
              </>
            )}

            {requisitos.length > 0 && (
              <>
                <Divider />
                <Section>
                  <STitle>Requisitos</STitle>
                  <BulletList>
                    {requisitos.map((r, i) => <BulletItem key={i}>{r}</BulletItem>)}
                  </BulletList>
                </Section>
              </>
            )}

            {diferenciais.length > 0 && (
              <>
                <Divider />
                <Section>
                  <STitle>Diferenciais</STitle>
                  <BulletList>
                    {diferenciais.map((d, i) => <BulletItem key={i}>{d}</BulletItem>)}
                  </BulletList>
                </Section>
              </>
            )}

            {beneficios.length > 0 && (
              <>
                <Divider />
                <Section>
                  <STitle>Benefícios</STitle>
                  <BulletList>
                    {beneficios.map((b, i) => <BulletItem key={i}>{b}</BulletItem>)}
                  </BulletList>
                </Section>
              </>
            )}

            {processo.length > 0 && (
              <>
                <Divider />
                <Section>
                  <STitle>Processo seletivo</STitle>
                  <ProcessList>
                    {processo.map((p, i) => <ProcessItem key={i}>{p}</ProcessItem>)}
                  </ProcessList>
                </Section>
              </>
            )}
          </div>

          {/* ── SIDEBAR ── */}
          <Sidebar aria-label="Informações e ações da vaga">
            <SideCard>
              <ApplyBtn href={`/candidatar/${job.slug}`}>
                Candidatar-me <span aria-hidden="true">→</span>
              </ApplyBtn>
            </SideCard>

            <SideCard>
              <SideTitle>Detalhes</SideTitle>
              {job.area && (
                <InfoRow>
                  <InfoLabel>Área</InfoLabel>
                  <InfoValue>{job.area}</InfoValue>
                </InfoRow>
              )}
              {job.regime && (
                <InfoRow>
                  <InfoLabel>Regime</InfoLabel>
                  <InfoValue>{job.regime}</InfoValue>
                </InfoRow>
              )}
              {job.nivel && (
                <InfoRow>
                  <InfoLabel>Nível</InfoLabel>
                  <InfoValue>{job.nivel}</InfoValue>
                </InfoRow>
              )}
              <InfoRow>
                <InfoLabel>Localidade</InfoLabel>
                <InfoValue>{job.local || "100% Remoto"}</InfoValue>
              </InfoRow>
              {job.faixa && (
                <InfoRow>
                  <InfoLabel>Faixa salarial</InfoLabel>
                  <SalaryValue>{job.faixa}</SalaryValue>
                </InfoRow>
              )}
            </SideCard>

            <SideCard>
              <SideTitle>Empresa</SideTitle>
              <InfoRow>
                <InfoLabel>Frente</InfoLabel>
                <InfoValue>{co.name}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Setor</InfoLabel>
                <InfoValue>{co.segment}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Contratação</InfoLabel>
                <InfoValue>{employer.razao}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>CNPJ</InfoLabel>
                <InfoValue>{employer.cnpj}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Grupo</InfoLabel>
                <InfoValue>Guebly Holding</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Site</InfoLabel>
                <InfoValue>
                  <a href="https://www.guebly.com.br" target="_blank" rel="noreferrer"
                    style={{ color: "var(--purple)", fontWeight: 700 }}>
                    guebly.com.br
                  </a>
                </InfoValue>
              </InfoRow>
            </SideCard>

            <ShareBtn onClick={handleShare} aria-live="polite">
              {shared
                ? <><span aria-hidden="true">✓</span> Link copiado!</>
                : <><span aria-hidden="true">↗</span> Compartilhar vaga</>
              }
            </ShareBtn>

            <StoryShare job={job} co={co} />
          </Sidebar>
        </Layout>
      </Wrap>
      <Footer />
    </Page>
  );
}
